/**
 * Where your agents are, as one line each (RFC 0336 3f, S10).
 *
 * Asking the World agent what is happening costs a turn to learn something the
 * log already says. So the World view draws a line per session that needs you
 * or is doing something: the question it is asking, the step it is on, or how
 * it finished. Read off each project's registry and the bounded header every
 * session carries (`projectSessionStripActivity`), never written by an agent,
 * so a line cannot go stale and costs nobody a turn.
 *
 * PURE, so which sessions get a line, in what order and saying what, is
 * testable without a repo. The hook that feeds it is `useWorldLines`.
 */

import {
  isSessionDone,
  parseQuestionBody,
  type QuestionBody,
  type SessionRunStatus,
  type SessionStripActivity,
} from '@mod/core/model'
import type { WorldSessionRow } from '../../services/world/useWorldSessions'

/**
 * Most urgent first: the one asking you, then work moving, then results you
 * have not seen, then the rest of the work in flight, where each left off.
 */
export type WorldLineState = 'asking' | 'working' | 'done' | 'waiting'

export interface WorldLine {
  sessionId: string
  title: string
  projectId: string
  projectName: string
  /** When the registry last saw it move, for the line's age. */
  updatedAt?: number
  state: WorldLineState
  /** The question, the step in plain words, or the first sentence of its
   *  last message: the result, or where it left off. */
  text: string
  /** The question itself, so a selected line can be answered in place. */
  question?: { interactionId: string; body: QuestionBody }
  /** The whole last message, for the selected or hovered line. */
  full?: string
}

/** How many lines show before the list scrolls. */
export const WORLD_LINE_LIMIT = 5

/** A session that gets a line, before its document is read. */
export interface LineCandidate {
  row: WorldSessionRow
  state: WorldLineState
}

/** Most urgent first. Exported so a line whose state changes on reading can
 *  be put back in its place. */
export const RANK: Record<WorldLineState, number> = {
  asking: 0,
  working: 1,
  done: 2,
  waiting: 3,
}

/**
 * Which sessions get a line, most urgent first.
 *
 * Every session not filed as done has one: the work in flight, asking and
 * working first. An unknown status is drawn as quiet, never as busy. A
 * session filed as done, the World agent itself,
 * and anything in `exclude` (what is open in the space you are in, which you
 * can already see) are left out. So are sessions another agent fanned out,
 * unless the World agent started them: six sub-sessions of one turn are six
 * lines nobody asked for.
 */
export function lineCandidates(
  rows: readonly WorldSessionRow[],
  opts: {
    worldAgentId: string | null
    exclude?: ReadonlySet<string>
    finished?: ReadonlySet<string>
  },
): LineCandidate[] {
  const out: LineCandidate[] = []
  for (const row of rows) {
    if (isSessionDone(row)) continue
    if (row.id === opts.worldAgentId) continue
    if (opts.exclude?.has(row.id)) continue
    if (row.parentSessionId && row.parentSessionId !== opts.worldAgentId)
      continue
    out.push({
      row,
      state: lineState(row, opts.finished?.has(row.id) ?? false),
    })
  }
  return out.sort(
    (a, b) =>
      RANK[a.state] - RANK[b.state] ||
      (b.row.updatedAt ?? 0) - (a.row.updatedAt ?? 0),
  )
}

function lineState(row: WorldSessionRow, finished: boolean): WorldLineState {
  const status = row.status
  if (status === 'asking') return 'asking'
  if (status === 'running' || status === 'queued') return 'working'
  if (status === 'idle' && finished) return 'done'
  // Everything else not filed is work in flight, waiting on a next move.
  // Most of the time that is most of the World, since agents stop between
  // turns. An unknown status lands here too: never drawn as busy.
  return 'waiting'
}

/**
 * Sessions that stopped working while this window watched.
 *
 * A session seen asking or working that is now idle has finished, and stays
 * finished until it is seen (`seen`). One idle on first sight is not news:
 * it finished before you were here to miss it.
 */
export function nextFinished(
  finished: ReadonlySet<string>,
  before: ReadonlyMap<string, SessionRunStatus | undefined>,
  rows: readonly WorldSessionRow[],
): Set<string> {
  const next = new Set(finished)
  for (const row of rows) {
    const was = before.get(row.id)
    if (row.status === 'idle' && was && was !== 'idle') next.add(row.id)
    else if (row.status && row.status !== 'idle') next.delete(row.id)
  }
  return next
}

/**
 * A question is shown only while the session still has it. The registry may
 * say asking long after the answer landed, if the writer that would have
 * cleared it is gone; the session's header is cleared by the answer itself.
 * `activity` undefined means the session is not read yet: keep the line.
 */
export function stillAsking(
  activity: SessionStripActivity | null | undefined,
): boolean {
  return activity === undefined || activity?.kind === 'question'
}

/** What a line says, from the session's bounded header. */
export function lineText(
  state: WorldLineState,
  status: SessionRunStatus | undefined,
  /** `undefined`: the session is not read yet, so a quiet line says nothing. */
  activity: SessionStripActivity | null | undefined,
): Pick<WorldLine, 'text' | 'question' | 'full'> {
  if (state === 'asking') {
    if (activity?.kind === 'question') {
      const body = parseQuestionBody(activity.bodyJson)
      const prompt = body.questions[0]?.prompt
      if (prompt)
        return {
          text: prompt,
          question: { interactionId: activity.interactionId, body },
        }
    }
    return { text: 'needs your answer' }
  }
  if (state === 'working') {
    if (status === 'queued') return { text: 'waiting for a worker' }
    if (activity?.kind === 'tool')
      return { text: plainStep(activity.name, activity.summary) }
    if (activity?.kind === 'text') return { text: firstLine(activity.text) }
    return { text: 'working' }
  }
  if (activity?.kind === 'reply')
    return { text: firstSentence(activity.text), full: activity.text.trim() }
  if (activity === undefined) return { text: '' }
  return { text: state === 'done' ? 'finished' : 'nothing said yet' }
}

/** Verbs for the tools every harness has, so a step reads as a sentence. */
const VERBS: Record<string, string> = {
  bash: 'running',
  shell: 'running',
  read: 'reading',
  read_file: 'reading',
  read_document: 'reading',
  edit: 'editing',
  multiedit: 'editing',
  write: 'writing',
  patch_document: 'editing',
  update_document: 'editing',
  grep: 'searching for',
  glob: 'looking for',
  search: 'searching for',
  websearch: 'searching the web for',
  webfetch: 'reading',
  task: 'handing off',
  agent: 'handing off',
  todowrite: 'updating its plan',
  ask_question: 'asking',
  create_task: 'adding a task',
  update_task: 'updating a task',
  complete_task: 'completing a task',
  comment: 'commenting',
  send_message: 'messaging',
  start_session: 'starting a session',
  set_view: 'opening',
  create_document: 'writing',
  askuserquestion: 'asking',
}

/**
 * A tool call in plain words: "running corepack pnpm test", "editing
 * notices.ts". A path is cut to its file name, since the line is read at a
 * glance; an unknown tool keeps its own name.
 */
export function plainStep(name: string, summary?: string): string {
  const verb = VERBS[name.toLowerCase()]
  const what = summary ? shortPath(summary.trim()) : ''
  if (!verb) return what ? `${name} ${what}` : name
  return what ? `${verb} ${what}` : verb
}

function shortPath(text: string): string {
  return /^[~./]?[\w.@-]*\/[\w./@-]+$/.test(text)
    ? (text.split('/').filter(Boolean).at(-1) ?? text)
    : text
}

/**
 * How a session finished, as a glance: its first sentence, not its first
 * paragraph. A reply written as one paragraph is otherwise all of it.
 */
export function firstSentence(text: string): string {
  const line = firstLine(text)
  const end = line.search(/[.!?](\s|$)/)
  return end === -1 ? line : line.slice(0, end + 1)
}

/** How long ago, as a line shows it: now, 4m, 2h, 3d. */
export function lineAge(at: number | undefined, now: number): string {
  if (at == null) return ''
  const minutes = Math.floor(Math.max(0, now - at) / 60_000)
  if (minutes < 1) return 'now'
  if (minutes < 60) return `${minutes}m`
  const hours = Math.floor(minutes / 60)
  return hours < 24 ? `${hours}h` : `${Math.floor(hours / 24)}d`
}

function firstLine(text: string): string {
  return (
    text
      .split('\n')
      .map((line) => line.trim())
      .find(Boolean) ?? ''
  )
}
